/*
 * First-visit "this is a demo store" modal.
 *
 * Opens a <dialog> with showModal(), so focus trapping, background inert-ness,
 * Escape-to-close and top-layer stacking are the platform's job rather than
 * ours. The same choice assets/gn-calendly.js makes.
 *
 * Remembering the dismissal is the whole point: "first visit" has to survive
 * navigation, or the modal fires on every page and becomes the thing a visitor
 * is fighting instead of reading.
 */
(() => {
  'use strict';

  /* Every notice on the page, not just the first.
     Two sections now use this file — the first-visit notice and the cart-drawer
     API explainer — and each renders its own <script> tag, so this IIFE runs
     twice. It used to querySelector a single root, which meant BOTH runs bound
     the same first dialog and the second one was never initialised at all: the
     cart notice was in the DOM and simply had no code behind it.
     The dataset guard makes the duplicate run a no-op instead of a double-bind. */
  function setup(root) {
    if (root.dataset.gnNoticeReady) return;
    root.dataset.gnNoticeReady = '1';

  let config = {};
  try {
    config = JSON.parse(root.getAttribute('data-gn-demo-notice')) || {};
  } catch {
    // A malformed config should not cost the visitor a broken page — fall
    // through to the defaults below.
  }

  const key = config.key || 'gn-demo-notice';
  const frequency = config.frequency || 'once';
  const delay = Number.isFinite(config.delay) ? config.delay : 700;

  // localStorage throws in Safari private mode rather than returning null, and
  // an exception here would take the modal down with it. Storage being
  // unavailable should mean "show it", not "break".
  const store = frequency === 'session' ? 'sessionStorage' : 'localStorage';

  function seen() {
    if (frequency === 'always') return false;
    try {
      return window[store].getItem(key) === '1';
    } catch {
      return false;
    }
  }

  function remember() {
    if (frequency === 'always') return;
    try {
      window[store].setItem(key, '1');
    } catch {
      // Nothing to do — the modal simply shows again next time.
    }
  }

  function close() {
    remember();
    root.classList.add('gn-demo--closing');
    // Let the exit transition finish before the dialog leaves the top layer,
    // otherwise it vanishes instantly and the animation is never seen.
    const done = () => {
      root.classList.remove('gn-demo--closing');
      if (root.open) root.close();
    };
    const card = root.querySelector('.gn-demo__card');
    if (!card || !matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      done();
      return;
    }
    card.addEventListener('transitionend', done, { once: true });
    // A transition that never fires (display swapped, element detached) would
    // otherwise leave the dialog stuck open.
    setTimeout(done, 400);
  }

  function open() {
    if (root.open) return;
    root.showModal();
    root.classList.add('gn-demo--open');
  }

  root.querySelectorAll('[data-gn-demo-close]').forEach((el) => {
    el.addEventListener('click', close);
  });

  // Escape fires `cancel` before `close`; intercept it so dismissal is
  // remembered the same way the buttons remember it.
  root.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  // Clicking the backdrop. The dialog element itself fills the viewport, so a
  // click whose target IS the dialog (rather than the card inside it) landed
  // outside the card.
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });

  // The secondary action opens Calendly over the top of this modal. Two
  // top-layer dialogs stack unpredictably, so step out of the way first — and
  // count that as having seen the notice.
  const secondary = root.querySelector('[data-gn-calendly]');
  if (secondary) secondary.addEventListener('click', close);

  if (seen()) return;

  const start = () => setTimeout(open, delay);

  /* Two ways in. `first-visit` fires once the page has painted; `cart-open`
     waits for the drawer, which is the only moment the thing this notice
     describes is actually on screen.

     The drawer is a <dialog> whose `open` attribute is the state worth
     watching, and the drawer section can hydrate after this script parses —
     hence observing the document rather than the dialog directly. */
  if ((config.trigger || 'first-visit') === 'cart-open') {
    const isOpen = () => !!document.querySelector('#cart-drawer > dialog[open]');
    if (isOpen()) {
      start();
      return;
    }
    const watch = new MutationObserver(() => {
      if (seen() || !isOpen()) return;
      watch.disconnect();
      start();
    });
    watch.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open'],
    });
    return;
  }

    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  document.querySelectorAll('[data-gn-demo-notice]').forEach(setup);
})();
